<template>
  <div v-if="!isLoading">
    <div class="row pt-2">
      <div class="col-12">
        <div class="booking-container mt-3 mb-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap"
          >
            <h4
              v-html="
                sanitize(
                  $t('PRODUCTS.TEXT.INCLUDED_SESSIONS', {
                    numSessions: product.number_of_included_sessions,
                  })
                )
              "
            />
            <h4
              v-if="privateSessionData.has_private_space"
              v-html="
                sanitize(
                  $t('PRODUCTS.TEXT.AVAILABLE_BOOKINGS', usedSessionData)
                )
              "
            />
          </div>
          <div
            v-if="privateSessionLimitReached"
            class="bg-brand-color-03 text-center rounded p-5 mt-3"
          >
            <div v-if="privateSessionData.has_private_space">
              <div class="c-charcoal font-weight-bold">
                {{ $t('PRODUCTS.TEXT.YOU_BOOKED_ALL_AVAILABLE_SESSIONS') }}
              </div>
              <div class="mt-4">
                <router-link :to="{ name: 'studio-shop' }">
                  <HMButton color="brand">
                    {{ $t('GENERAL.BUTTON.GO_TO_SHOP') }}
                  </HMButton>
                </router-link>
              </div>
            </div>
            <div v-else>
              <div class="c-charcoal font-weight-bold">
                {{ $t('PRODUCTS.TEXT.YOU_HAVE_NO_AVAILABLE_SESSIONS') }}
              </div>
              <div class="mt-4">
                <router-link
                  :to="{
                    name: 'studio-product-checkout',
                    params: { product_id: this.$route.params.product_id },
                  }"
                >
                  <HMButton color="brand">
                    {{ $t('GENERAL.BUTTON.GO_TO_SHOP') }}
                  </HMButton>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else>
            <PrivateSchedulingCalendar
              class="mt-4"
              v-if="product.use_internal_scheduling"
              :studioURL="studioUrl"
              :productId="product.id"
              :duration="product.time_slot_duration"
              @booked="refreshData"
            />
            <div
              ref="calendly-wrapper"
              class="calendly-wrapper"
              v-if="product.calendly_enabled"
            ></div>
            <iframe
              :src="getAcuityURL"
              :key="acuityKey"
              width="100%"
              height="800"
              frameBorder="0"
              v-if="product.acuity_enabled && getAcuityURL"
              name="acuityiFrame"
            ></iframe>
            <div
              class="text-primary text-center"
              v-if="product.acuity_enabled && !getAcuityURL"
            >
              <span>
                {{ $t('PRODUCTS.TEXT.CAN_NOT_LOAD_ACUITY_SCHEDULING') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import PrivateSchedulingCalendar from './PrivateSchedulingCalendar';
import { errorToMessage } from '../../../core/utils/errors';
import { sanitize } from '@/core/utils/utils';
import { HMButton } from '../common';

export default Vue.extend({
  name: 'StudioProductPrivateSessions',
  components: {
    PrivateSchedulingCalendar,
    HMButton,
  },
  data() {
    return {
      isLoading: true,
      privateSessionData: null,
      acuityKey: 0,
    };
  },
  computed: {
    ...mapGetters({
      product: 'product/product',
      user: 'user/user',
      studentId: 'user/studentId',
      canViewProductContent: 'product/canViewProductContent',
    }),
    usedSessionData() {
      if (!this.privateSessionData) return null;
      const {
        number_of_used_sessions,
        number_of_included_sessions,
      } = this.privateSessionData;
      return {
        usedSessions: number_of_used_sessions,
        includedSessions: number_of_included_sessions,
        availableSessions:
          number_of_included_sessions - number_of_used_sessions,
      };
    },
    getAcuityURL() {
      if (
        !(
          this.product.acuity_calendar_id &&
          this.product.acuity_owner_id &&
          this.product.acuity_form_id
        )
      )
        return;

      const baseURL = 'https://app.acuityscheduling.com/schedule.php?';
      const params = {
        calendarID: this.product.acuity_calendar_id,
        owner: this.product.acuity_owner_id,
        firstName: this.user.student.first_name,
        lastName: this.user.student.last_name,
        email: this.user.email,
        quantity: 1,
      };
      if (this.product.acuity_appointment_type_id) {
        params.appointmentType = this.product.acuity_appointment_type_id;
      }
      const str = `&field:${this.product.acuity_form_id}=${this.studentId},${this.product.id}`;
      return baseURL + '&' + new URLSearchParams(params).toString() + str;
    },
    studioUrl() {
      return getStudioURL(this.$route);
    },
    privateSessionLimitReached() {
      if (!this.usedSessionData) return true;
      return this.usedSessionData.availableSessions <= 0;
    },
  },
  methods: {
    sanitize,
    isCalendlyEvent(e) {
      return e.data.event && e.data.event.indexOf('calendly') === 0;
    },
    calendlyEventListener(e) {
      switch (e.data.event) {
        case 'calendly.date_and_time_selected':
          this.refreshData();
          break;
        case 'calendly.event_scheduled':
          this.privateSessionData.number_of_used_sessions += 1;
          break;
      }
    },
    acuityEventListener(e) {
      if (
        e.data.toString().includes('scrollTo') &&
        e.origin === 'https://app.acuityscheduling.com'
      ) {
        this.refreshData();
      }
      if (
        e.data === 'load:' &&
        e.origin === 'https://app.acuityscheduling.com'
      ) {
        this.acuityKey += 1;
        this.privateSessionData.number_of_used_sessions += 1;
      }
    },
    async refreshData() {
      const data = {
        studioURL: this.studioUrl,
        productId: this.product.id,
      };
      try {
        this.privateSessionData = await this.$store.dispatch(
          'product/canAddPrivateSession',
          data
        );
      } catch (error) {
        // ignore
      }
      try {
        await this.$store.dispatch('product/getProductDetail', {
          studioURL: this.studioUrl,
          productId: this.product.id,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title:
            'Failed to refresh product information. Please refresh your page!',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    initCalendly() {
      const data = JSON.stringify({
        student_id: this.studentId,
        product_id: this.product.id,
      });

      const prefill = {
        name: this.user.user_full_name,
        email: this.user.email,
      };
      this.$nextTick(() => {
        window.Calendly.initInlineWidget({
          url: `${this.product.calendly_url}?hide_event_type_details=1`,
          parentElement: this.$refs['calendly-wrapper'],
          prefill,
          utm: {
            utmCampaign: '',
            utmSource: '',
            utmMedium: '',
            utmContent: data,
            utmTerm: '',
          },
        });
      });
    },
    getScheduler() {
      if (this.privateSessionData && this.product.calendly_enabled) {
        this.initCalendly();
        window.addEventListener('message', this.calendlyEventListener);
      } else if (this.privateSessionData && this.product.acuity_enabled) {
        window.addEventListener('message', this.acuityEventListener);
      }
    },
  },
  async mounted() {
    await this.refreshData();
    this.isLoading = false;
    this.getScheduler();
  },
  destroyed() {
    window.removeEventListener('message', this.calendlyEventListener);
    window.removeEventListener('message', this.acuityEventListener);
  },
});
</script>

<style scoped>
.calendly-wrapper {
  min-width: 320px;
  height: 700px;
}
</style>
